import { useCallback, useId, useRef, type KeyboardEvent } from 'react'

type Kind = 'tab' | 'radio'

interface UseRovingGroupOptions<T extends string> {
  /** Valores en el orden en que se pintan. */
  items: readonly T[]
  /** Valor seleccionado (controlado). `null` = ninguno (solo radios). */
  value: T | null
  onChange: (next: T) => void
  /** `tab` → tablist/tab/tabpanel; `radio` → radiogroup/radio. */
  kind: Kind
  /** Etiqueta accesible del grupo. */
  label?: string
}

/**
 * Grupo de opciones con "roving tabindex" (headless, sin estilos): pestañas o radios
 * hechos con botones. Da lo que el patrón ARIA exige y los `<button role="tab">` sueltos
 * no tenían:
 * - un solo tab-stop (el seleccionado; el primero si no hay ninguno);
 * - flechas ←/→ y ↑/↓ para moverse (con vuelta), Inicio/Fin a los extremos; la
 *   selección sigue al foco (activación automática, lo normal para pocas opciones);
 * - roles y `aria-selected`/`aria-checked`, y en pestañas `aria-controls` ↔
 *   `aria-labelledby` con su panel.
 *
 * Uso: `getGroupProps()` en el contenedor, `getItemProps(v)` en cada botón y, en
 * pestañas, `getPanelProps(v)` en el panel. El aspecto lo pone el caller.
 */
export function useRovingGroup<T extends string>({
  items,
  value,
  onChange,
  kind,
  label,
}: UseRovingGroupOptions<T>) {
  const baseId = useId()
  const refs = useRef(new Map<T, HTMLElement>())

  const itemId = useCallback((v: T) => `${baseId}-${kind}-${v}`, [baseId, kind])
  const panelId = useCallback((v: T) => `${baseId}-panel-${v}`, [baseId])

  const focusAndSelect = useCallback(
    (next: T) => {
      refs.current.get(next)?.focus()
      onChange(next)
    },
    [onChange],
  )

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      if (items.length === 0) return
      const current = items.indexOf((value ?? items[0]) as T)
      const last = items.length - 1
      let next: number | null = null
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          next = current >= last ? 0 : current + 1
          break
        case 'ArrowLeft':
        case 'ArrowUp':
          next = current <= 0 ? last : current - 1
          break
        case 'Home':
          next = 0
          break
        case 'End':
          next = last
          break
        default:
          return
      }
      e.preventDefault()
      focusAndSelect(items[next] as T)
    },
    [items, value, focusAndSelect],
  )

  const tabStop = value ?? items[0] ?? null

  const getGroupProps = () => ({
    role: kind === 'tab' ? 'tablist' : 'radiogroup',
    'aria-label': label,
    onKeyDown,
  })

  const getItemProps = (v: T) => {
    const selected = v === value
    return {
      ref: (el: HTMLElement | null) => {
        if (el) refs.current.set(v, el)
        else refs.current.delete(v)
      },
      id: itemId(v),
      role: kind,
      type: 'button' as const,
      tabIndex: v === tabStop ? 0 : -1,
      onClick: () => onChange(v),
      ...(kind === 'tab'
        ? { 'aria-selected': selected, 'aria-controls': panelId(v) }
        : { 'aria-checked': selected }),
    }
  }

  const getPanelProps = (v: T) => ({
    role: 'tabpanel' as const,
    id: panelId(v),
    'aria-labelledby': itemId(v),
    tabIndex: 0,
  })

  return { getGroupProps, getItemProps, getPanelProps }
}
